import AuthApi from 'app/services/core/login.service'
import EntitlementService from 'app/services/core/entitlement.service'
import EventingService from 'app/services/analitics/event.service'
import {
  getUserAssets,
  getUserPrograms,
  hasDEForCKLA,
  hasICAndCKLAOnUserContentLevel,
} from 'app/services/api/retail.service'
import ContentTokenService from 'app/services/api/content-token.service'
import EnvConstants, {
  getLocation,
  Locations,
} from 'app/services/core/config.service'
import promiseHandler from 'app/utils/promise-handler'
import { hasNoRedirectParam } from 'app/utils/url'
import { GRADES } from 'app/types/grades.types'
import { UserSession } from 'app/types/auth.types'
import { ProgramFilter } from 'app/types/home-content-button.types'
import {
  listenForTokensFromDesmos,
  shouldGetTokenFromParentWindow,
} from 'app/utils/iframe'
import { READONLY_ROLE } from 'app/constants/entitlement.constant'

const contentTokenService = new ContentTokenService()

class AppLogin {
  userSession: UserSession
  userGrades: GRADES[]
  userHubPrograms: Record<string, string[]>
  userFilters: Record<string, Array<ProgramFilter>>

  constructor() {
    this.userSession = {} as UserSession
    this.userGrades = []
    this.userHubPrograms = {}
    this.userFilters = {}
  }

  async createKeycloakClient() {
    if (shouldGetTokenFromParentWindow()) {
      const config = await listenForTokensFromDesmos()
      await AuthApi.initForDesmosIframe(config)
    } else {
      await AuthApi.init()
    }

    const [error, userSession] = await promiseHandler(AuthApi.getCurrentUser())
    if (error || !userSession) {
      AuthApi.logout()
      throw error
    }

    this.userSession = userSession
  }

  async checkAccessToken() {
    const [accessTokenError, contentTokenRes] = await promiseHandler(
      contentTokenService.getContentToken(AuthApi.getAccessToken())
    )
    if (accessTokenError) throw accessTokenError
    if (contentTokenRes && contentTokenRes.data) {
      this.userSession.contentToken = contentTokenRes.data.contentToken
    }
    contentTokenService.createTokenHeartbeat(AuthApi.logout)
  }

  async redirectToStudentHome() {
    if (this.userSession.isStudent && !hasNoRedirectParam()) {
      try {
        const hasDE = await hasDEForCKLA()
        const hasICAndCKLA = await hasICAndCKLAOnUserContentLevel()
        if (hasDE || hasICAndCKLA) {
          window.location.replace(EnvConstants.STUDENT_HOME_URL)
        }
      } catch (e) {
        console.error(e)
      }
    }
  }

  static redirectToLearning() {
    if (getLocation() === Locations.CKLA) {
      const { pathname, href } = window.location
      let urlParam = ''
      if (href.includes('?')) {
        urlParam = href.slice(href.indexOf('?'))
      }
      window.location.replace(
        `${EnvConstants.LEARNING_URL}${pathname}${urlParam}`
      )
    }
  }

  async checkEntitlement() {
    const [assetsError, assets] = await promiseHandler(getUserAssets())
    if (assetsError) {
      throw assetsError
    }

    if (!EntitlementService.isEntitled(assets)) {
      throw new Error('User is not entitlement')
    }

    const [programsError, programs] = await promiseHandler(getUserPrograms())
    if (programsError) {
      throw programsError
    }

    this.userSession.roles = AuthApi.getRoles()
    this.userGrades = EntitlementService.getGradesFromAssets(assets)
    this.userHubPrograms = EntitlementService.getHubPrograms(assets, programs)
    this.userFilters = EntitlementService.getFilters(
      this.userGrades,
      this.userHubPrograms
    )
    this.userSession.permissions = EntitlementService.getUserPermissions(assets)
    this.userSession.readers = EntitlementService.getReaders(assets)
    this.userSession.isTeacher = EntitlementService.isTeacherInRoles(
      this.userSession.roles
    )
    this.userSession.isStudent = EntitlementService.isStudentInRoles(
      this.userSession.roles
    )
    this.userSession.isReadOnly =
      this.userSession.roles?.includes(READONLY_ROLE)
  }

  submitEvent() {
    if (!this.userSession) return

    EventingService.init(this.userSession.ampId)
    EventingService.sendLoginEvent()
  }

  async performLogin() {
    AppLogin.redirectToLearning()
    await this.createKeycloakClient()
    await this.checkAccessToken()
    await this.checkEntitlement()

    if (!this.userSession.isReadOnly) {
      await this.redirectToStudentHome()
      this.submitEvent()
    }

    return {
      userSession: this.userSession,
      grades: this.userGrades,
      hubPrograms: this.userHubPrograms,
      filters: this.userFilters,
    }
  }
}

export default new AppLogin()
